import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { ConfirmDialog } from 'components/confirm-dialog';
import { ServiceMode } from 'generated/graphql-gateway';
import { useLocationContext } from 'state/location';
import {
  IIncentiveEvaluationResult,
  IncentiveEvaluationErrorCodes,
} from 'state/loyalty/hooks/types';

const isInvalidServiceModePickup = (invalidServiceMode: IIncentiveEvaluationResult) => {
  return invalidServiceMode.targetValue.some(
    (target: string) =>
      target === ServiceMode.CURBSIDE ||
      target === ServiceMode.EAT_IN ||
      target === ServiceMode.TAKEOUT ||
      target === ServiceMode.DRIVE_THRU
  );
};

const isInvalidServiceModeDelivery = (invalidServiceMode: IIncentiveEvaluationResult) => {
  return invalidServiceMode.targetValue.some((target: string) => target === ServiceMode.DELIVERY);
};

interface InvalidServiceModeModalProps {
  incentiveEvaluationResults: IIncentiveEvaluationResult[];
}

export const InvalidServiceModeModal = ({
  incentiveEvaluationResults,
}: InvalidServiceModeModalProps) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { location, setStoreLocatorCallbackUrl } = useLocationContext();
  const [invalidServiceMode, setInvalidServiceMode] = useState<IIncentiveEvaluationResult>();

  useEffect(() => {
    const serviceModeError = incentiveEvaluationResults?.find(
      ({ code }) => code === IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE
    );

    setInvalidServiceMode(serviceModeError);
  }, [incentiveEvaluationResults]);

  if (!invalidServiceMode) {
    return null;
  }

  const invalidPickup = isInvalidServiceModePickup(invalidServiceMode);
  const invalidDelivery = isInvalidServiceModeDelivery(invalidServiceMode);

  if (!invalidPickup && !invalidDelivery) {
    return null;
  }

  const handleConfirmSwitchServiceMode = () => {
    setStoreLocatorCallbackUrl(location.pathname);
    if (invalidPickup) {
      navigate(formatMessage({ id: 'routes.storeLocator' }));
    } else {
      navigate(formatMessage({ id: 'routes.storeLocator.address' }));
    }
  };

  const handleCancelSwitchServiceMode = () => {
    setInvalidServiceMode(undefined);
  };

  return (
    <ConfirmDialog
      heading={formatMessage({
        id: invalidPickup ? 'switchServiceModeToPickupHeader' : 'switchServiceModeToDeliveryHeader',
      })}
      body={formatMessage({
        id: invalidPickup ? 'switchServiceModeToPickupBody' : 'switchServiceModeToDeliveryBody',
      })}
      confirmLabel={formatMessage({
        id: invalidPickup ? 'switchServiceModeToPickupButton' : 'switchServiceModeToDeliveryButton',
      })}
      cancelLabel={formatMessage({ id: 'cancel' })}
      onConfirm={handleConfirmSwitchServiceMode}
      onCancel={handleCancelSwitchServiceMode}
      onDismiss={handleCancelSwitchServiceMode}
      modalAppearanceEventMessage="Confirm Change Service Mode"
    />
  );
};
